import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useGuidance } from './GuidanceContext';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function GuidanceSheet({
  variant = 'sheet',
  isOpen,
  onDismiss,
  title,
  children,
  primaryAction,
  secondaryAction,
  dismissKey,
  header,
}) {
  const titleId = useId();
  const titleRef = useRef(null);
  const dialogRef = useRef(null);
  const dragState = useRef(null);
  const { markSeen } = useGuidance();

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.activeElement;
    requestAnimationFrame(() => titleRef.current?.focus());

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = [...(dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) || [])];
      if (focusables.length === 0) {
        e.preventDefault();
        titleRef.current?.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === titleRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === titleRef.current) {
        e.preventDefault();
        first.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
    // dismiss intentionally stays outside this effect dependency set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  function markDismissed() {
    if (dismissKey) markSeen(dismissKey, true);
  }

  function dismiss() {
    markDismissed();
    onDismiss?.();
  }

  function runAction(action) {
    markDismissed();
    action?.onClick?.();
  }

  function onDragStart(e) {
    if (variant !== 'sheet') return;
    dragState.current = { startY: e.touches[0].clientY, startTime: Date.now() };
    if (dialogRef.current) dialogRef.current.style.transition = 'none';
  }

  function onDragMove(e) {
    if (variant !== 'sheet' || !dragState.current || !dialogRef.current) return;
    const dy = e.touches[0].clientY - dragState.current.startY;
    if (dy < 0) return;
    dialogRef.current.style.transform = `translateY(${dy}px)`;
  }

  function onDragEnd(e) {
    if (variant !== 'sheet' || !dragState.current || !dialogRef.current) return;
    const dy = e.changedTouches[0].clientY - dragState.current.startY;
    const elapsed = Date.now() - dragState.current.startTime;
    const velocity = dy / Math.max(elapsed, 1);
    dragState.current = null;

    if (dy > 80 || velocity > 0.5) {
      dismiss();
    } else {
      dialogRef.current.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
      dialogRef.current.style.transform = 'translateY(0)';
    }
  }

  const dialog = (
    <>
      {variant === 'sheet' && (
        <div className="guidance-sheet-scrim" onClick={dismiss} />
      )}
      <div
        className={`guidance-sheet guidance-sheet--${variant}`}
        role="dialog"
        aria-modal={variant === 'fullscreen' ? 'true' : undefined}
        aria-labelledby={titleId}
        ref={dialogRef}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        {variant === 'sheet' && <div className="guidance-sheet-handle" />}
        <div className="guidance-sheet-content">
          {header && <div className="guidance-sheet-header-slot">{header}</div>}
          <h2 id={titleId} ref={titleRef} tabIndex="-1" className="guidance-sheet-title">
            {title}
          </h2>
          <div className="guidance-sheet-body">
            {children}
          </div>
          {(primaryAction || secondaryAction) && (
            <div className="guidance-sheet-actions">
              {primaryAction && (
                <button type="button" className="btn btn-primary guidance-primary-action" onClick={() => runAction(primaryAction)}>
                  {primaryAction.label}
                </button>
              )}
              {secondaryAction && (
                <button type="button" className="guidance-secondary-action" onClick={() => runAction(secondaryAction)}>
                  {secondaryAction.label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}
